import React, { ChangeEvent, Dispatch, SetStateAction, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Grid,
  TextField,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  MenuItem,
  Box,
  Select,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
  DroppableProvided,
} from 'react-beautiful-dnd';
import { theme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { LocationResponse, StockCheckDetails, StockCheckItem, StockCheckItemValue } from '../../../app/services/api/generated';
import { BarcodeScannerModal } from './BarcodeScanner/BarcodeScannerModal';
import ConfirmZoneSwitchModal from '../modals/ConfirmZoneSwitchModal';
import StockCountQuantityInput from './StockCountQuantityInput';
import OnlineOfflineSnackbar from './OnlineOfflineSnackbar';
import { StockCheckStatus } from '../../../enums/StockCheckStatus';
import useNswagClient from '../../../hooks/api/useNswagClient';
import useLogError from '../../../hooks/useLogError';
import { UserContext } from '../../../components/shared/useUser';
import PrintTable from './PrintTable';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import UpdateIcon from '@mui/icons-material/Update';

interface StockCheckDetailsProps {
  stockCheckDetails: StockCheckDetails,
  isScannerModalOpen: boolean,
  openScannerModal: () => void,
  zonesData: LocationResponse[],
  allZonesStockCheckItems: StockCheckItem[],
  saveReorder:boolean,
  setSaveReorder: Dispatch<SetStateAction<boolean>>,
  sortState: boolean | undefined,
  itemScanned: boolean,
  printAll: boolean,
  handleSortStateChange: () => void,
  setSaveOngoing: Dispatch<SetStateAction<boolean>>
  selectedZoneId: string,
  handleSelectedZoneChange: (event: SelectChangeEvent<string>) => void;
  allZonesEnabled: boolean,
  closeModal: () => void
  handleSavingDataUserBackOnline: () => void;
  // eslint-disable-next-line no-unused-vars
  handleAutosaveMessage: (changesSaved: boolean) => void;
  handleCloseSaveAlert: () => void;
  showSavedAlert: boolean
  onModifiedItemsChange: (modifiedItems: Set<number>) => void;
  isBlockerActive: boolean;
  // eslint-disable-next-line no-unused-vars
  onReorderStateChange: (hasUnsavedReorder: boolean) => void;
  hasBarcodeScanningPermission: boolean;
}

export interface TempCategoryItem {
  name: string;
  items: StockCheckItem[];
}


const StockCountDetails = forwardRef<{ getUpdatedValues: () => { scheduledDate: string, stockCheckItems: StockCheckItem[] } | null }, StockCheckDetailsProps>(({ stockCheckDetails, isScannerModalOpen, closeModal, zonesData, allZonesStockCheckItems, saveReorder, setSaveReorder, sortState, itemScanned, printAll, handleSortStateChange, setSaveOngoing, selectedZoneId, handleSelectedZoneChange, allZonesEnabled, handleSavingDataUserBackOnline, handleAutosaveMessage, handleCloseSaveAlert, showSavedAlert, onModifiedItemsChange, isBlockerActive, onReorderStateChange, hasBarcodeScanningPermission }, ref) => {
  const { t } = useTranslation('common');
  const { t: scanning } = useTranslation('stockCountScanning');
  const { saveStockCheckItem, saveStockCheckItemAtLocation, saveStockCountReorder } = useNswagClient();
  const { logError } = useLogError();
  const { hasPermissionTo } = useContext(UserContext);
  const stockCheckDetail = stockCheckDetails.stockCheck;
  const [scheduledDate, setScheduledDate] = useState<string>(stockCheckDetail?.plannedCountDate?.split('T')[0] ?? '');
  const [stockCheckItems, setStockCheckItems] = useState<StockCheckItem[]>(stockCheckDetail?.items ?? []);
  const [enableReorder, setEnableReorder] = useState<boolean>(sortState ?? false);
  const [sortedItems, setSortedItems] = useState<StockCheckItem[]>(stockCheckDetail?.items ?? []);
  const [zoneItems, setZoneItems] = useState<StockCheckItem[]>(stockCheckDetail?.items ?? []);
  const [showRowNumber, setShowRowNumber]= useState<boolean>(sortState ?? false);
  const [sortStates, setSortStates] = useState(sortState ?? false); 
  const [modifiedItems, setModifiedItems] = useState<Set<number>>(new Set());
  const [lastFocusedItemId, setLastFocusedItemId] = useState<number | null>(null);
  const [firstPageOpened, setFirstPageOpened] = useState<boolean>(false);
  const [isStockReordered, setIsStockReordered] = useState<boolean | undefined>(false);
  const [hasBeenReordered, setHasBeenReordered] = useState<boolean>(false);
  const stockCheckItemsRef = useRef(stockCheckItems);
  const modifiedItemsRef = useRef(modifiedItems);
  const savingItemsRef = useRef(new Set<number>());
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    onReorderStateChange(hasBeenReordered);
  }, [hasBeenReordered]);

  const currentZone  =  parseInt(selectedZoneId, 10);
  const [showZoneSwitchWarning, setShowZoneSwitchWarning] = useState<boolean>(false);
  const [pendingZoneId, setPendingZoneId] = useState<string>('');

  //snackbar modal state
  const [snackBarDuration, setSnackBarDuration] = useState<number | null>(6000);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');

  const INACTIVITY_TIMEOUT = 60000; // 1 min on milliseconds

  useEffect(() => {
    if (sortState !== undefined) {
      setSortStates(sortState);
      setEnableReorder(sortState);
      if (sortState) {
        setShowRowNumber(true);
      }
    }
  }, [sortState]);

  useEffect(() => {
    stockCheckItemsRef.current = stockCheckItems;
  }, [stockCheckItems]);

  useEffect(() => {
    modifiedItemsRef.current = modifiedItems;
    if (modifiedItems.size === 0) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    handleAutosaveMessage(false);
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(() => {
      modifiedItemsRef.current.forEach(itemId => autosaveItem(itemId));
      inactivityTimerRef.current = null;
    }, INACTIVITY_TIMEOUT);

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [modifiedItems, stockCheckItems]);

  useEffect(() => {
    setScheduledDate(stockCheckDetail?.plannedCountDate?.split('T')[0] ?? '');
    setStockCheckItems(stockCheckDetail?.items ?? []);
    stockCheckItemsRef.current = stockCheckDetail?.items ?? [];
    const resetModifiedItems = new Set<number>();
    setModifiedItems(resetModifiedItems);
    modifiedItemsRef.current = resetModifiedItems;
    setLastFocusedItemId(null);
    notifyParentFormChanged(resetModifiedItems);

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, [stockCheckDetails]);

  useEffect(() => () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);


  const notifyParentFormChanged = (modifiedItemsTemp: Set<number>) => {
    onModifiedItemsChange(modifiedItemsTemp);
  };

  const normalizeItemForSave = (item: StockCheckItem): StockCheckItem => ({
    ...item,
    itemValues: item.itemValues?.map(value => {
      const countQuantity = value.countQuantity == null || typeof value.countQuantity === 'number'
        ? value.countQuantity
        : Number(value.countQuantity);

      return {
        ...value,
        countQuantity: typeof countQuantity === 'number' && Number.isNaN(countQuantity) ? 0 : countQuantity,
      };
    }),
  });

  const autosaveItem = (itemId: number) => {
    const item = stockCheckItemsRef.current.find(item => item.id === itemId);

    if (item) {
      handleItemAutosave(normalizeItemForSave(item));
    }
    else {
      handleAutosaveMessage(false);
    }
  };

  const handleItemAutosave = (itemToSave: StockCheckItem) => {
    const itemId = itemToSave.id;
    if (itemId === undefined || savingItemsRef.current.has(itemId)) {
      return;
    }
    savingItemsRef.current.add(itemId);

    const handleItemSaved = () => {
      savingItemsRef.current.delete(itemId);
      const latestItem = stockCheckItemsRef.current.find(item => item.id === itemId);
      const hasNewerEdits = latestItem !== undefined &&
        JSON.stringify(normalizeItemForSave(latestItem).itemValues) !== JSON.stringify(itemToSave.itemValues);

      setModifiedItems(prev => {
        const updatedItems = new Set(prev);
        if (!hasNewerEdits) {
          updatedItems.delete(itemId);
        }

        modifiedItemsRef.current = updatedItems;
        if (updatedItems.size === 0 && inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
          inactivityTimerRef.current = null;
        }
        notifyParentFormChanged(updatedItems);
        handleAutosaveMessage(updatedItems.size === 0);
        return updatedItems;
      });
    };

    const handleSaveError = (err: unknown) => {
      savingItemsRef.current.delete(itemId);
      logError(err);
    };

    if (selectedZoneId == '-1') {
      saveStockCheckItem(stockCheckDetail?.id ?? 0, itemToSave)
        .then(() => {
          handleItemSaved();
        })
        .catch(handleSaveError);
    }
    else {
      saveStockCheckItemAtLocation(stockCheckDetail?.id ?? 0, parseInt(selectedZoneId), itemToSave)
        .then(() => {
          handleItemSaved();
        })
        .catch(handleSaveError);
    }
  };

  const handleStockCheckItemsFromScanner: React.Dispatch<React.SetStateAction<StockCheckItem[]>> = (value) => {
    setStockCheckItems(prevItems => {
      const updatedItems = typeof value === 'function'
        ? value(prevItems)
        : value;
      stockCheckItemsRef.current = updatedItems;

      const changedItemIds = updatedItems
        .filter((updatedItem, index) => {
          const previousItem = prevItems[index];
          return JSON.stringify(updatedItem.itemValues) !== JSON.stringify(previousItem?.itemValues);
        })
        .map(item => item.id)
        .filter((id): id is number => id !== undefined);

      if (changedItemIds.length > 0) {
        setModifiedItems(prev => {
          const updatedModifiedItems = new Set(prev);
          changedItemIds.forEach(id => updatedModifiedItems.add(id));
          modifiedItemsRef.current = updatedModifiedItems;
          notifyParentFormChanged(updatedModifiedItems);
          return updatedModifiedItems;
        });

        handleCloseSaveAlert();
      }

      return updatedItems;
    });
  };

  // to create a ref for the child component
  useImperativeHandle(ref, () => ({
    getUpdatedValues: () => ({
      scheduledDate,
      stockCheckItems,
    }),
  }), [scheduledDate, stockCheckItems]);

  const handleScheduledDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setScheduledDate(event.target.value);
  };

  const updateItemValues = (item: StockCheckItem, itemId: number | undefined, valueId: number | undefined, newQuantity: number | string | undefined) => {

    if (item.id === itemId) {
      return {
        ...item,
        itemValues: item.itemValues?.map(value => {
          if (value.id === valueId) {
            return {
              ...value,
              countQuantity: newQuantity as number,
            };
          }
          return value;
        }),
      };
    }
    return item;
  };

  const handleQuantityChange = (itemId: number | undefined, valueId: number | undefined, newQuantityString: string) => {
    // Allow only positive numbers (including decimals) and empty string
    if (/^\d*\.?\d*$/.test(newQuantityString) || newQuantityString === '') {

      setStockCheckItems(prevItems => {
        const updatedItems = prevItems.map(item => updateItemValues(item, itemId, valueId, newQuantityString));
        stockCheckItemsRef.current = updatedItems;
        return updatedItems;
      });

      setSortedItems(prevItems => {
        const updatedItems = prevItems.map(item => updateItemValues(item, itemId, valueId, newQuantityString));
        return updatedItems;
      });

    }
    if (itemId !== undefined && !modifiedItems.has(itemId)) {
      setModifiedItems(prev => {
        const updatedItems = new Set(prev);
        updatedItems.add(itemId);
        modifiedItemsRef.current = updatedItems;
        notifyParentFormChanged(updatedItems);
        return updatedItems;
      });
    }
    handleCloseSaveAlert();
  };

  const handleQuantityBlur = (itemId: number | undefined, valueId: number | undefined, newQuantityString: string) => {
    // Skip processing if the leave page blocker modal is active because we choose there whether to save the changes or not
    if (isBlockerActive) {
      return;
    }

    const value = Number(newQuantityString);
    const newQuantity = isNaN(value) ? 0 : value;
    const updatedItems = stockCheckItemsRef.current.map(item => updateItemValues(item, itemId, valueId, newQuantity));
    stockCheckItemsRef.current = updatedItems;
    setStockCheckItems(updatedItems);

    if (itemId !== undefined && modifiedItems.has(itemId)) {
      autosaveItem(itemId);
    }
  };

  const sortUoms = (itemValues: StockCheckItemValue[], primaryUom: string | undefined) => {
    return itemValues.sort((a, b) => {
      // If neither or both are 1, sort the rest by conversionToPrimary from lowest to highest
      if (primaryUom && a.uom == primaryUom) {
        return -1;
      }

      // First, put items with conversionToPrimary === 1 at the front
      if (a.conversionToPrimary === 1 && b.conversionToPrimary !== 1) {
        return -1;
      } else if (a.conversionToPrimary !== 1 && b.conversionToPrimary === 1) {
        return 1;
      }

      // If neither or both are 1, sort the rest by conversionToPrimary from lowest to highest
      if (a.conversionToPrimary !== undefined && b.conversionToPrimary !== undefined) {
        return a.conversionToPrimary - b.conversionToPrimary;
      }
      return 0;
    });
  };

  const itemsGroupedByCategory = (items: StockCheckItem[]) => {
    const categories: { [key: string]: TempCategoryItem } = {};
    if (items) {
      for (const item of items) {
        if (item.category) {
          let categoryKey = item.category;
          categoryKey = categoryKey.trim();
          categoryKey = categoryKey.toUpperCase();
          categoryKey = categoryKey.replace(/[\W]/gi, '');

          if (categories[categoryKey]?.items) {
            categories[categoryKey].items.push(item);
          } else {
            categories[categoryKey] = {
              name: item.category,
              items: [item],
            };
          }
        }
      }
      const sortedCategories = Object.values(categories).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      sortedCategories.forEach((category) => {
        category.items.sort((a, b) =>
          (a.description ?? '').localeCompare(b.description ?? ''),
        );
      });
      let globalRowNumber = 1;
      sortedCategories.forEach((category) => {
        category.items = category.items.map((item) => ({
          ...item,
          rowNumber: globalRowNumber++,
        }));
      });

      return sortedCategories;
    }
    return [];
  };

  const itemsGroupedByCountZone = (items: StockCheckItem[]) => {
    const countZones: { [key: string] : TempCategoryItem } = {};
    if (!items || !zonesData || zonesData.length === 0) return [];

    zonesData.forEach(zone => {
      countZones[zone.name!] = { name: zone.name!, items: [] };
    });

    for (const zoneName in countZones) {
      const zone = zonesData.find(z => z.name === zoneName);
      const zoneId = zone?.id;
      const zoneIdStr = zoneId?.toString() ?? '';

      let zoneItems: StockCheckItem[];

      if (selectedZoneId !== '-1' && zoneIdStr === selectedZoneId) {
        zoneItems = items.map(item => ({ ...item }));
      } else if (zoneReorderedData.has(zoneIdStr)) {
        zoneItems = zoneReorderedData.get(zoneIdStr)!.map(item => ({ ...item }));
      } else {
        zoneItems = items.map(item => ({ ...item }));
        zoneItems.sort((a, b) => {
          const aRow = a.itemLocationValues?.find(loc => loc.locationId === zoneId)?.rowNumber;
          const bRow = b.itemLocationValues?.find(loc => loc.locationId === zoneId)?.rowNumber;

          if (aRow != null && bRow != null) return aRow - bRow;
          if (aRow != null) return -1;
          if (bRow != null) return 1;

          return sortByCategory(a, b);
        });
      }

      countZones[zoneName].items = zoneItems.map((item, index) => ({
        ...item,
        rowNumber: index + 1,
      }));
    }

    return Object.values(countZones);
  };

  useEffect(() => {
    setFirstPageOpened(true);
    itemsGroupedByCategory(stockCheckDetail?.items!);
    setIsStockReordered(stockCheckDetail?.reorderEnabled);
    const anyItemHasRowNumber = stockCheckDetail?.items!.some(item => item.rowNumber != null);
    setShowRowNumber(anyItemHasRowNumber || sortStates);
    const currentOrderState = stockCheckDetail?.reorderEnabled;

    if (sortStates) {
      setEnableReorder(true);
      return;
    }

    if (stockCheckDetail?.status === StockCheckStatus.Counting){

      if (stockCheckDetail?.reorderEnabled && !hasPermissionTo(['CountItemReorderingWrite'])) {
        setEnableReorder(true);
      }
      else {
        currentOrderState ? setEnableReorder(true) : setEnableReorder(false);
      }
    }
    if (stockCheckDetail?.status === StockCheckStatus.Pending) {
      if (stockCheckDetail?.reorderEnabled && !hasPermissionTo(['CountItemReorderingWrite'])) {
        setEnableReorder(true);
      }
      else{
        setEnableReorder(false);
      }
    }
   
  }, [stockCheckDetail, sortStates]);

  const enableStockReorder = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnableReorder(event.target.checked);
    setSortStates(event.target.checked);
    setShowRowNumber(true);
    if (hasPermissionTo(['CountItemReorderingWrite'])) {
      handleSortStateChange();
    }
  };

  const reorder = (list: StockCheckItem[] | undefined, startIndex: number, endIndex: number) => {
    const result = Array.from(list ?? []);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };
  const [zoneReorderedData, setZoneReorderedData] = useState<Map<string, StockCheckItem[]>>(new Map());

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    if (result.source.index !== result.destination.index) {
      setHasBeenReordered(true);
    }

    const newItems = reorder(sortedItems, result.source.index, result.destination.index);
    const updatedItems = newItems.map((item, index) => {
      return {
        ...item,
        rowNumber: index + 1,
      };
    });
    const newZoneItems = newItems.map((item, index) => {
      return {
        ...item,
        rowNumber: index + 1,
      };
    });

    setZoneReorderedData(prev => new Map(prev.set(selectedZoneId, updatedItems)));
    setZoneItems(newZoneItems);
    setSortedItems(updatedItems);
  };

  useEffect(() => {
    if (saveReorder) {
      saveStockReorder();
    }
  }, [saveReorder]);

  const checkDefaultRowNumbers = (items: StockCheckItem[]) => {

    const existingRowNumbers = items.some(item => item.rowNumber != null);
    const updatedItems = items.map((item, index) => ({
      ...item,
      rownumber: existingRowNumbers ? item.rowNumber : index + 1,
    }));
    return updatedItems;
  };

  const setSnackBarProps = (severity: 'error' | 'warning' | 'info' | 'success', message: string) => {
    severity === 'success' ? setSnackBarDuration(6000) : setSnackBarDuration(null);
    setIsSnackBarOpen(true);
    setSnackBarSeverity(severity);
    setSnackbarMessage(message);
  };

  const handleItemFocus = (itemId: number) => {
    if (lastFocusedItemId !== null && lastFocusedItemId !== itemId && modifiedItems.has(lastFocusedItemId)) {
      autosaveItem(lastFocusedItemId);
    }
    setLastFocusedItemId(itemId);
  };

  const saveStockReorder = () => {
    if (!hasBeenReordered) {
      setSaveReorder(false);
      setSnackBarProps('info', 'No changes to save');
      return;
    }

    const zoneId = selectedZoneId == '-1' ? undefined : parseInt(selectedZoneId, 10);
    const reorderedItem = selectedZoneId == '-1' ? checkDefaultRowNumbers(sortedItems) : zoneItems;
    setSnackBarProps('info', 'Saving in progress');

    saveStockCountReorder(stockCheckDetail?.id!, zoneId, reorderedItem).then(() => {
      setSnackBarProps('success', 'Save successful');
      setHasBeenReordered(false);
      setTimeout(() => {
        setSaveOngoing(true);
      }, 1000);
    })
      .catch((error) => {
        setSnackBarProps('error', `${t('errorMessage')}: ${error}`);
      });
  };

  const sortItems = () => {

    // If we have reordered data for this zone, use it
    if (zoneReorderedData.has(selectedZoneId) && selectedZoneId !== '-1') {
      return zoneReorderedData.get(selectedZoneId)!;
    }

    // Otherwise use the original sorting logic
    return sortedItems.sort((a, b) => {
      const hasExistingRowNumbers = sortedItems.some(item => item.rowNumber != null && item.rowNumber > 0);

      if (selectedZoneId === '-1') {
        if (!hasExistingRowNumbers) {
          return sortByCategory(a, b);
        }
        return a.rowNumber! - b.rowNumber!;
      } else if (!firstPageOpened) {
        return 0;
      } else {
        const locationA = a.itemLocationValues?.find(x => x.locationId === currentZone);
        const locationB = b.itemLocationValues?.find(x => x.locationId === currentZone);
        setFirstPageOpened(false);

        if (!locationA || !locationB) {
          return sortByCategory(a, b);

        }
        return locationA?.rowNumber! - locationB?.rowNumber!;
      }
    });
  };
  const sortByCategory = (a: StockCheckItem, b: StockCheckItem): number => {
    const categoryComparison = (a.category || '').localeCompare(b.category || '');
    if (categoryComparison !== 0) {
      return categoryComparison;
    }

    return (a.description || '').localeCompare(b.description || '');
  };


  useEffect(() => {
    setSortedItems(stockCheckItems);
  }, [itemScanned]);

  const canShowDragHandle = () => {
    return stockCheckDetail?.status === StockCheckStatus.Pending && hasPermissionTo(['CountItemReorderingWrite']);
  };

  // allZonesStockCheckItems holds aggregated (all-zones) countQuantity values in itemValues,
  // so when printing a zone that is not the currently loaded one we must rebuild itemValues
  // from that zone's itemLocationValues entries, otherwise the aggregated total is shown instead
  // of the quantity counted in that specific zone.
  const withZoneSpecificValues = (items: StockCheckItem[], zoneId: number | undefined): StockCheckItem[] => {
    return items.map(item => {
      const locationValues = item.itemLocationValues?.filter(loc => loc.locationId === zoneId);
      if (!locationValues || locationValues.length === 0) {
        return {
          ...item,
          itemValues: item.itemValues?.map(value => ({
            ...value,
            countQuantity: undefined,
          })),
        };
      }

      return {
        ...item,
        itemValues: locationValues.map(loc => ({
          id: loc.id,
          stockCheckItemId: loc.stockCheckItemId,
          uom: loc.uom,
          conversionToPrimary: loc.conversionToPrimary,
          conversionToEach: loc.conversionToEach,
          countQuantity: loc.countQuantity,
        })),
      };
    });
  };

  const getPrintData = (): TempCategoryItem[] => {
    if (!zonesData || zonesData.length === 0) return [];

    return zonesData.map(zone => {
      const zoneId = zone.id;
      const isCurrentZone = String(selectedZoneId) !== '-1' && String(zoneId) === String(selectedZoneId);

      let zoneItems: StockCheckItem[];

      if (!enableReorder) {
        const baseItems = isCurrentZone
          ? [...sortedItems]
          : withZoneSpecificValues([...(allZonesStockCheckItems.length > 0 ? allZonesStockCheckItems : sortedItems)], zoneId);
        zoneItems = baseItems.sort((a, b) => sortByCategory(a, b));
      } else if (isCurrentZone) {
        // Use reordered data from drag-and-drop if available
        if (zoneReorderedData.has(selectedZoneId) && selectedZoneId !== '-1') {
          zoneItems = [...zoneReorderedData.get(selectedZoneId)!];
        } else {
          // Apply the same sorting logic as sortItems() for consistent print order
          zoneItems = [...sortedItems];
          if (selectedZoneId === '-1') {
            const hasExistingRowNumbers = zoneItems.some(item => item.rowNumber != null && item.rowNumber > 0);
            if (!hasExistingRowNumbers) {
              zoneItems.sort((a, b) => sortByCategory(a, b));
            } else {
              zoneItems.sort((a, b) => a.rowNumber! - b.rowNumber!);
            }
          } else {
            zoneItems.sort((a, b) => {
              const locationA = a.itemLocationValues?.find(x => x.locationId === currentZone);
              const locationB = b.itemLocationValues?.find(x => x.locationId === currentZone);
              if (!locationA || !locationB) return sortByCategory(a, b);
              return locationA.rowNumber! - locationB.rowNumber!;
            });
          }
        }
      } else {
        const zoneIdStr = String(zoneId);
        if (zoneReorderedData.has(zoneIdStr)) {
          zoneItems = withZoneSpecificValues([...zoneReorderedData.get(zoneIdStr)!], zoneId);
        } else {
          // Use all-zones items which have itemLocationValues for every zone,
          // falling back to sortedItems if all-zones data is not available
          const baseItems = allZonesStockCheckItems.length > 0 ? allZonesStockCheckItems : sortedItems;
          zoneItems = withZoneSpecificValues([...baseItems], zoneId);
          zoneItems.sort((a, b) => {
            const aRow = a.itemLocationValues?.find(loc => loc.locationId === zoneId)?.rowNumber;
            const bRow = b.itemLocationValues?.find(loc => loc.locationId === zoneId)?.rowNumber;
            if (aRow != null && bRow != null) return aRow - bRow;
            if (aRow != null) return -1;
            if (bRow != null) return 1;
            return sortByCategory(a, b);
          });
        }
      }

      return {
        name: zone.name!,
        items: zoneItems.map((item, index) => ({ ...item, rowNumber: index + 1 })),
      };
    });
  };

  const printData = getPrintData();
  
  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
  };
  const cancelZoneSwitch = () => {
    setShowZoneSwitchWarning(false);
    setPendingZoneId('');
  };


  const handleZoneChangeWithWarning = (event: SelectChangeEvent<string>) => {
    const newZoneId = event.target.value;

    if (hasBeenReordered && enableReorder) {
      setPendingZoneId(newZoneId);
      setShowZoneSwitchWarning(true);
    } else {
      handleSelectedZoneChange(event);
      setHasBeenReordered(false);
    }
  };

  const confirmZoneSwitch = () => {
    setShowZoneSwitchWarning(false);

    setZoneReorderedData(prev => {
      const newMap = new Map(prev);
      newMap.delete(selectedZoneId);
      return newMap;
    });

    setHasBeenReordered(false);

    const syntheticEvent = {
      target: { value: pendingZoneId },
    } as SelectChangeEvent<string>;

    handleSelectedZoneChange(syntheticEvent);
    setPendingZoneId('');
  };

  return (
    <>
      <style>
        {`
          @media print {
            * {
                overflow: visible !important;
              }

              body {
                height: 100% !important;
              }

              .container {
                overflow: visible !important;
              }
                table {
              page-break-inside: auto;
            }
          
            .quantity-fields-wrapper {
              gap: 0px !important; /* Reduce spacing between fields */
            }

            .quantity-column {
              padding: 0px !important; /* Reduce cell padding */
            }

            .quantity-fields-wrapper div {
              min-width: 4px !important; /* Reduce input wrapper width */
              gap: 0px !important; /* Reduce spacing between elements */
              margin: 0 !important; /* Remove unnecessary margins */
            }
            .MuiTableRow-root > td {
              padding-right: 4px !important; /* Add spacing between columns */
            }
            }

        `}
      </style>
      <Grid container
        spacing={2}
        alignItems="center">
        <Grid item
          xs={12}
          sm={5}
          md={3}
          container
          spacing={1}
          sx={{ textAlign: 'left' }}>
          <Grid item
            xs={12}>
            <Typography variant='textLG'>{t('createdBy')}</Typography>
          </Grid>
          <Grid item
            xs={12}>
            <TextField
              id="outlined-basic"
              variant="outlined"
              value={stockCheckDetail?.createdBy}
              disabled
              sx={{
                width: '90%',
                backgroundColor: theme.palette.custom.gray[200],
              }}
            />
          </Grid>
        </Grid>

        {/* Scheduled Date Section */}
        <Grid item
          xs={12}
          sm={5}
          md={3}
          container
          spacing={1}
          sx={{ textAlign: 'left' }}>
          <Grid item
            xs={12}>
            <Typography variant='textLG'>{t('scheduledDate')}</Typography>
          </Grid>
          <Grid item
            xs={12}>
            <TextField
              type="date"
              required
              value={scheduledDate}
              onChange={handleScheduledDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{ width: '90%' }}
            />
          </Grid>
        </Grid>

        {/* Stock Count Zones */}
        {zonesData?.length > 0 && (
          <Grid item
            xs={12}
            sm={5}
            md={3}
            container
            spacing={1}
            sx={{ textAlign: 'left' }}>
            <Grid item
              xs={12}>
              <Typography variant='textLG'>{t('stockCountZonesDdl')}</Typography>
            </Grid>
            <Grid item
              xs={12}>
              <Select
                id="stock-count-zones-ddl"
                sx={{ width: '90%' }}
                value={selectedZoneId}
                onChange={handleZoneChangeWithWarning}
              >
                <MenuItem value="-1">
                  <em>{t('stockCountZonesDdlAll')}</em>
                </MenuItem>
                {zonesData.map((zone) => (
                  <MenuItem key={zone.id}
                    value={zone.id}>{zone.name}</MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        )}
        {stockCheckDetail?.status === StockCheckStatus.Pending && hasPermissionTo(['CountItemReorderingWrite']) && (
          <Grid item
            xs={12}
            sm={5}
            md={3}
            container
            spacing={1}
            sx={{ textAlign: 'right', mt: '15px' }}>
            <Grid item
              xs={12}
              sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <FormControlLabel
                control={<Switch checked={enableReorder}
                  onChange={enableStockReorder} />}
                label='Product Sorting'
                labelPlacement="end"
              />
            </Grid>
          </Grid>
        )}
      </Grid>

      {stockCheckDetail?.status === StockCheckStatus.Counting && <Grid item
        xs={12}
        md={zonesData?.length > 0 ? 2 : 5}
        alignSelf={{ xs: 'center', md: 'end' }}
        textAlign={{ xs: 'center', md: 'end' }}
        sx={{ mt: '20px' }}>

        <OnlineOfflineSnackbar
          onOnline={handleSavingDataUserBackOnline} />
      </Grid>
      }
      {!enableReorder && (
        <Grid item
          className="stock-count-categories"
          xs={12}
          sx={{ mt: '20px' }}>
          {itemsGroupedByCategory(stockCheckItems).map((category: TempCategoryItem) => (
            <Grid container
              key={category.name}
              className='stockcount-table-container'
              sx={{
                border: '1px solid',
                boxShadow: '0 1px 2px 1px #1018280D',
                paddingTop: '8px', pb: '4px', marginTop: '16px', borderRadius: '12px', borderColor: theme.palette.custom.gray[300],
              }}>
              <Grid item
                className="stock-count-categories"
                xs={12}
                sx={{ mt: '0px' }}>
                <Box
                  key={category.name}
                  display='flex'
                  flexDirection='column'
                  my={4}
                  marginTop='0'
                  className="category">
                  <Box
                    display="flex"
                    flexDirection="row"
                    p={4}
                    className="title"
                  >
                    <Typography
                      sx={{ mb: '4px', ml: '8px' }}
                    >
                      {category.name}
                    </Typography>
                  </Box>
                  <TableContainer component={'div'}
                    id={`stock-items-table-container-${category.name}`}
                    className={'stockcount-table'}
                    key={category.name}>
                    <Table
                      size="small"
                      aria-label="a table">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '8px', padding: '0' }}></TableCell>
                          <TableCell
                            align="left"
                          >{t('rowNumber')}</TableCell>
                          <TableCell
                            align="left"
                          >
                            {t('skuNumber')}
                          </TableCell>
                          <TableCell
                            align="left"
                            className='category-table-cell'>
                            {t('skuDescription')}
                          </TableCell>
                          <TableCell
                            align="left"
                          >
                            {t('quantity')}
                          </TableCell>
                          {hasBarcodeScanningPermission && stockCheckDetail?.status !== StockCheckStatus.Pending && <TableCell
                            align="left"
                          >
                            {scanning('scanStatus')}
                          </TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {category.items
                          .map((item) => (
                            <TableRow
                              sx={{
                                '&:last-child td, &:last-child th': { border: 0 }, borderBottom: '1px solid',
                                borderBottomColor: theme.palette.custom.gray[200],
                                backgroundColor: item.id && modifiedItems.has(item.id) && !showSavedAlert ? theme.palette.common.white : theme.palette.custom.gray[50],
                              }}
                              tabIndex={0} // Make row focusable
                              onFocus={() => handleItemFocus(item.id ?? 0)} // Handle focus
                              className='item'
                              key={item.id}>
                              <TableCell sx={{ width: '8px', padding: '0' }}></TableCell>
                              <TableCell>{item.rowNumber}</TableCell>
                              <TableCell>
                                {item.itemNumber}
                              </TableCell>
                              <TableCell
                                sx={{ width: '2x' }}
                                className='category-table-cell'
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {item.isNew && (
                                    <Tooltip
                                      title={t('newItemIndicator')}
                                      arrow
                                    >
                                      <span>⭐ </span>
                                    </Tooltip>
                                  )}

                                  {item.newItemNumber && (
                                    <Tooltip
                                      title={t('inTransition', { newItemNumber: item.newItemNumber })}
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            maxWidth: 'none',
                                            whiteSpace: 'nowrap',
                                          },
                                        },
                                      }}
                                    >
                                      <UpdateIcon sx={{ fontSize: 18, mr: 1, mb: 2 }} />
                                    </Tooltip>
                                  )}

                                  {item.description}
                                </Box>
                              </TableCell>
                              
                              <TableCell className="quantity-column"
                                sx={{ width: '3x', height: '60px' }}>
                                {item.itemValues && (
                                  <div className="quantity-fields-wrapper"
                                    style={{ display: 'flex', flexWrap: 'wrap', height: '100%', gap: '8px' }}>
                                    {sortUoms(item.itemValues, item.primaryUom).map((value, index) => (
                                      <div key={index}
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          alignItems: 'center',
                                          minWidth: '90px',
                                          justifyContent: 'flex-start',
                                          gap: '4px',

                                        }}>
                                        <StockCountQuantityInput
                                          item={item}
                                          value={value}
                                          stockCheckDetail={stockCheckDetail}
                                          selectedZoneId={selectedZoneId}
                                          allZonesEnabled={allZonesEnabled}
                                          handleQuantityChange={handleQuantityChange}
                                          handleQuantityBlur={handleQuantityBlur}

                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </TableCell>
                              {hasBarcodeScanningPermission && stockCheckDetail?.status !== StockCheckStatus.Pending && (
                                <TableCell>
                                  {item.scanStatus == undefined ? '' : item.scanStatus == 0 ? scanning('unsuccessful') : scanning('successful')}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}
      {enableReorder && (
        <Grid item
          className="stock-count-categories"
          xs={12}
          sx={{ mt: '10px' }}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Grid container
              className='stockcount-table-container'
              sx={{
                border: '1px solid',
                boxShadow: '0 1px 2px 1px #1018280D',
                paddingTop: '8px',
                pb: '4px',
                marginTop: '16px',
                borderRadius: '12px',
                borderColor: theme.palette.custom.gray[300],
              }}
            >
              <Grid item
                xs={12}>
                <TableContainer component={'div'}
                  className={'stockcount-table'}>
                  <Table size="small"
                    aria-label="a table">
                    <TableHead>
                      <TableRow>
                        {canShowDragHandle() && <TableCell sx={{ width: '1x' }}
                          align="left"></TableCell>}
                        {showRowNumber && <TableCell align="left">{t('rowNumber')}</TableCell>}
                        <TableCell align="left">{t('skuNumber')}</TableCell>
                        <TableCell>{t('stockOnHand.category')}</TableCell>
                        <TableCell align="left">{t('skuDescription')}</TableCell>
                        <TableCell align="left">{t('quantity')}</TableCell>
                        {hasBarcodeScanningPermission && stockCheckDetail?.status !== StockCheckStatus.Pending && <TableCell
                          align="left"
                        >
                          {scanning('scanStatus')}
                        </TableCell>}
                      </TableRow>
                    </TableHead>
                    <Droppable droppableId="droppable">
                      {(provided: DroppableProvided) => (
                        <TableBody {...provided.droppableProps}
                          ref={provided.innerRef}>
                          {sortItems().map((item: StockCheckItem, index: number) => (
                            <Draggable key={item.id}
                              draggableId={item.id!.toString()}
                              index={index}
                              isDragDisabled={!hasPermissionTo(['CountItemReorderingWrite'])}>
                              {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                <TableRow
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    '&:last-child td, &:last-child th': { border: 0 },
                                    borderBottom: '1px solid',
                                    borderBottomColor: theme.palette.custom.gray[200],
                                    backgroundColor: snapshot.isDragging
                                      ? theme.palette.custom.gray[100]
                                      : theme.palette.custom.gray[50],
                                    cursor: 'pointer',
                                  }}
                                  tabIndex={0}
                                  onFocus={() => {
                                    if (stockCheckDetail?.status === StockCheckStatus.Counting) {
                                      handleItemFocus(item.id ?? 0);
                                    }
                                  }}
                                >
                                  {canShowDragHandle() && (
                                    <TableCell>
                                      <IconButton
                                        {...provided.dragHandleProps}
                                        sx={{ cursor: snapshot.isDragging ? 'grabbing' : 'grab' }}
                                      >
                                        <DragIndicatorIcon />
                                      </IconButton>
                                    </TableCell>
                                  )}
                                  {showRowNumber && <TableCell>{index + 1}</TableCell>}
                                  <TableCell className="sku-number">{item.itemNumber}</TableCell>
                                  <TableCell className="category">{item.category}</TableCell>
                                  <TableCell className="description">
                                    {item.isNew &&
                                      <Tooltip title={t('newItemIndicator')}
                                        arrow>
                                        <span>⭐ </span>
                                      </Tooltip>
                                    }
                                    {item.description}
                                  </TableCell>
                                  <TableCell className="quantity-column"
                                    sx={{ width: '3x', height: '60px' }}>
                                    {item.itemValues && (
                                      <div className="quantity-fields-wrapper"
                                        style={{ display: 'flex', flexWrap: 'wrap', height: '100%', gap: '8px' }}>
                                        {sortUoms(item.itemValues, item.primaryUom).map((value, index) => (
                                          <div key={index}
                                            style={{
                                              display: 'flex',
                                              flexDirection: 'row',
                                              alignItems: 'center',
                                              minWidth: '90px',
                                              justifyContent: 'flex-start',
                                              gap: '4px',

                                            }}>
                                            <StockCountQuantityInput
                                              item={item}
                                              value={value}
                                              stockCheckDetail={stockCheckDetail}
                                              selectedZoneId={selectedZoneId}
                                              allZonesEnabled={allZonesEnabled}
                                              handleQuantityChange={handleQuantityChange}
                                              handleQuantityBlur={handleQuantityBlur}
                                            />
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </TableCell>
                                  {hasBarcodeScanningPermission && stockCheckDetail?.status !== StockCheckStatus.Pending && <TableCell>
                                    {item.scanStatus == undefined ? '' : item.scanStatus == 0 ? scanning('unsuccessful') : scanning('successful')}
                                  </TableCell>}
                                </TableRow>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </DragDropContext>
        </Grid>
      )}
      {/* This is used only for printing after reorder */}
      {printAll && (
        <PrintTable
          printData={printData}
          stockCheckDetails={stockCheckDetails}
          stockCheckDetail={stockCheckDetail}
          selectedZoneId={selectedZoneId}
          allZonesEnabled={allZonesEnabled}
          handleQuantityChange={handleQuantityChange}
          handleQuantityBlur={handleQuantityBlur}
        />
      )}
      {isScannerModalOpen ?
        <BarcodeScannerModal
          isScannerOpen={isScannerModalOpen}
          closeModal={closeModal}
          stockCheckItems={stockCheckItems}
          setStockCheckItems={handleStockCheckItemsFromScanner}
          stockCountName={stockCheckDetail?.name}
          stockCountId={stockCheckDetail?.id ?? 0}
          hasBarcodeScanningPermission={hasBarcodeScanningPermission}
        /> : null}

      <MessagesSnackbar onClose={closeSnackBar}
        open={isSnackBarOpen}
        severity={snackBarSeverity}
        message={snackbarMessage}
        duration={snackBarDuration}/>

      <ConfirmZoneSwitchModal
        open={showZoneSwitchWarning}
        onClose={cancelZoneSwitch}
        onConfirm={confirmZoneSwitch}
      />
    </>

  );
});

StockCountDetails.displayName = 'StockCountDetails';
export default StockCountDetails;

